import type { GlobalPcSetsWorkspaceItemPanel } from '../model/types'

import { useCallback, useMemo, useReducer } from 'react'

import { uuid } from '@/shared/tools/utils'

type PanelInit = Partial<Omit<GlobalPcSetsWorkspaceItemPanel, 'id'>>

export const createPanel = (
	panel?: PanelInit
): GlobalPcSetsWorkspaceItemPanel => ({
	pricingCampaignId: panel?.pricingCampaignId ?? null,
	part: panel?.part ?? 'settings',
	id: uuid(),
})
const initialState = {
	panels: [createPanel()],
}

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'add_panel':
			return {
				...state,
				panels: [...state.panels, createPanel(action.panel)],
			}
		case 'remove_panel': {
			const newPanels = state.panels.filter((panel) => panel.id !== action.id)

			return {
				...state,
				panels: newPanels?.length === 0 ? [createPanel()] : newPanels,
			}
		}
		case 'update_panel':
			return {
				...state,
				panels: state.panels.map((panel) =>
					panel.id === action.id ? { ...panel, ...action.data } : panel
				),
			}
		default:
			return state
	}
}

type State = {
	panels: GlobalPcSetsWorkspaceItemPanel[]
}
type Action =
	| {
			type: 'add_panel'
			panel?: PanelInit
	  }
	| {
			type: 'remove_panel'
			id: string
	  }
	| {
			type: 'update_panel'
			id: string
			data: Partial<Omit<GlobalPcSetsWorkspaceItemPanel, 'id'>>
	  }

export const usePanelsStore = () => {
	const [state, dispatch] = useReducer(reducer, initialState)
	const updatePanel = useCallback(
		(id: string, data: Partial<Omit<GlobalPcSetsWorkspaceItemPanel, 'id'>>) => {
			dispatch({ type: 'update_panel', id, data })
		},
		[dispatch]
	)
	const addPanel = useCallback(
		(panel?: PanelInit) => {
			dispatch({ type: 'add_panel', panel })
		},
		[dispatch]
	)
	const removePanel = useCallback(
		(id: string) => {
			dispatch({ type: 'remove_panel', id })
		},
		[dispatch]
	)

	return useMemo(
		() => ({
			panels: state.panels,
			updatePanel,
			addPanel,
			removePanel,
		}),
		[state.panels, updatePanel, addPanel, removePanel]
	)
}
